/**
 * AppLayout Component
 * Shared layout wrapper for all authenticated page routes except /chat and /onboarding.
 *
 * Desktop (md+): persistent 300px sidebar on the left, content area to its right.
 *                Sidebar open/closed state is lifted here so the content margin
 *                adjusts dynamically when the sidebar collapses.
 * Mobile (<md):  no visible sidebar by default; hamburger in AppHeader opens the
 *                sidebar as a temporary MUI Drawer.
 *
 * Accessibility: WCAG 2.1 AA
 * - sidebar nav landmark: aria-label="Site navigation" (via Box component="nav")
 * - main content area: component="main"
 */

import React, { useState, useEffect } from 'react';
import { Box, Drawer, Toolbar } from '@mui/material';
import { useLocation } from 'react-router-dom';
import AppHeader from './AppHeader';
import Sidebar from './Sidebar';
// Phase 999.100 W4 (D-16): global footer surfaces /about-ai + privacy/legal links.
import AppFooter from './AppFooter';

const SIDEBAR_WIDTH = 300;
// v1.1: collapsed = icon-only mini-rail (NOT fully hidden). Best-practice
// pattern (Notion / shadcn / uxpatterns.dev) - an always-visible rail with
// hover tooltips, rather than a fully-hidden sidebar + floating expand button.
const RAIL_WIDTH = 64;
const TRANSITION = 'width 0.225s cubic-bezier(0.4,0,0.6,1), margin-left 0.225s cubic-bezier(0.4,0,0.6,1)';

/** Per-user localStorage key for persisting the desktop collapse state. */
const collapseKey = (userId) => (userId ? `afterservice_sidebar_collapsed_${userId}` : null);

/**
 * AppLayout - layout shell for authenticated pages.
 *
 * @param {{ user: object, children: React.ReactNode, sessions: Array }} props
 */
export default function AppLayout({ user, children, sessions = [] }) {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);   // mobile temporary drawer

  // Desktop sidebar open/collapsed - persisted per-user in localStorage so the
  // rail remembers its state across sessions (best-practice). Default open.
  const userId = user?.userId || user?.sub || user?.username;
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const key = collapseKey(userId);
    if (!key) return true;
    try {
      return localStorage.getItem(key) !== 'collapsed';
    } catch {
      return true;
    }
  });

  // Toggle + persist. Stores 'collapsed' / 'open' so an absent key defaults open.
  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => {
      const next = !prev;
      const key = collapseKey(userId);
      if (key) {
        try {
          localStorage.setItem(key, next ? 'open' : 'collapsed');
        } catch {
          // quota / unavailable - non-fatal, state still updates in memory
        }
      }
      return next;
    });
  };

  // Close mobile drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Focus main content on route change - WCAG 2.4.3 Focus Order
  useEffect(() => {
    document.getElementById('main-content')?.focus();
  }, [location.pathname]);

  const noOpSessionProps = {
    sessions,
    currentSession: null,
    onSessionSelect: () => {},
    onSessionCreate: () => {},
    onSessionUpdate: () => {},
    onSessionDelete: () => {},
    onRefreshSessions: () => {},
  };

  // Collapsed = 64px mini-rail (icon-only), not 0 - the rail stays visible.
  const sidebarWidthPx = sidebarOpen ? SIDEBAR_WIDTH : RAIL_WIDTH;

  // P3 #23: ONE scroll root. On standard pages the document (html) scrolls,
  // so window.scrollTo and in-page anchors work. The chat route is the only
  // exception - it needs a definite-height <main> so the message list can
  // scroll internally while the composer stays pinned.
  const isChatRoute = location.pathname === '/assistant';

  return (
    <Box sx={{ display: 'flex', width: '100%', minWidth: 0, minHeight: '100vh' }}>

      {/* Skip navigation - WCAG 2.1 AA: allows keyboard users to bypass sidebar on every page */}
      <a
        href="#main-content"
        style={{
          position: 'absolute',
          left: '-9999px',
          zIndex: 9999,
          padding: '8px 16px',
          background: '#000',
          color: '#fff',
          textDecoration: 'none',
          borderRadius: '4px',
        }}
        onFocus={(e) => { e.target.style.left = '16px'; e.target.style.top = '16px'; }}
        onBlur={(e) => { e.target.style.left = '-9999px'; }}
      >
        Skip to main content
      </a>

      {/* Full-width command bar across the very top, above the sidebar.
          Per the design the header has no collapse control on desktop - the
          mobile hamburger opens the drawer; the rail collapses from its own
          control (Sidebar). */}
      <AppHeader
        user={user}
        onMobileDrawerToggle={() => setDrawerOpen(true)}
        sessions={noOpSessionProps.sessions}
        sidebarOpen={sidebarOpen}
      />

      {/* Persistent sidebar - desktop (md+) only. Starts BELOW the 64px header. */}
      <Box
        component="nav"
        aria-label="Site navigation"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: sidebarWidthPx,
          flexShrink: 0,
          position: 'fixed',
          left: 0,
          top: '64px',
          bottom: 0,
          zIndex: (t) => t.zIndex.drawer,
          overflow: 'hidden',
          transition: TRANSITION,
        }}
      >
        <Box sx={{ width: SIDEBAR_WIDTH, height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
          <Sidebar
            {...noOpSessionProps}
            user={user}
            mobileDrawerOpen={false}
            onMobileDrawerToggle={() => {}}
            sidebarOpen={sidebarOpen}
            onSidebarToggle={handleSidebarToggle}
          />
        </Box>
      </Box>

      {/* Temporary drawer - mobile (<md) only */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: false }}
        sx={{
          display: { md: 'none' },
          '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH },
        }}
      >
        <Box
          component="nav"
          aria-label="Site navigation"
          sx={{ width: SIDEBAR_WIDTH, overflow: 'hidden', position: 'relative' }}
        >
          <Sidebar
            {...noOpSessionProps}
            user={user}
            mobileDrawerOpen={drawerOpen}
            onMobileDrawerToggle={() => setDrawerOpen(false)}
          />
        </Box>
      </Drawer>

      {/* Content column - offset right of sidebar, transitions with it.
          AppHeader now lives at the top level (full-width), so it's not
          rendered here; the Toolbar spacer clears the fixed 64px header. */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          ml: { xs: 0, md: `${sidebarWidthPx}px` },
          minHeight: '100vh',
          transition: TRANSITION,
        }}
      >
        {/* Spacer so content is not hidden behind the fixed AppBar */}
        <Toolbar />
        <Box
          component="main"
          id="main-content"
          tabIndex={-1}
          sx={{
            flex: 1,
            width: '100%',
            overflowX: 'hidden',
            // Chat only: fixed-height inner scroller (see isChatRoute above).
            // Everywhere else <main> grows with content and the document scrolls.
            ...(isChatRoute ? { overflowY: 'auto', minHeight: 0 } : {}),
          }}
        >{children}</Box>
        {/* Phase 999.100 W4: global footer with /about-ai + privacy/legal nav. */}
        <AppFooter />
      </Box>

    </Box>
  );
}
